import moment from 'moment';
import { AccountStatus } from '../../Enum/AccountStatus';
import { capitalizeFirstLetter } from '../../Utils/Function';
import { getActiveBrandConfig, getPathClientBrandConfig } from '../../config/environment.config';

// ─── Fiche client hybride ─────────────────────────────────────────────────────
// Les clients dont l'inscription n'est pas finalisée gardent la popup
// « Consulter » ; les clients inscrits ont une page dédiée avec son URL.

/** Statuts d'inscription non finalisée : pas encore de fiche dédiée. */
export const NON_FINALIZED_STATUSES: string[] = [
  AccountStatus.INVITATION_PENDING,
  AccountStatus.CONTACT_COMPLETE,
  AccountStatus.APPAREILLAGE_NF,
  AccountStatus.APPAREILLAGE_EC,
  AccountStatus.PENDING,
];

/** État de navigation posé par la liste : le retour de la fiche revient à la liste telle qu'elle était. */
export interface ClientDetailLocationState {
  fromList?: boolean;
}

/** Onglet de la liste (ClientPage) porté par l'URL : ?onglet=archives, absent pour les actifs. */
export const CLIENT_LIST_TAB_PARAM = 'onglet';
export const CLIENT_LIST_ARCHIVES_TAB = 'archives';

/** Liste des clients, sur l'onglet des archivés si demandé. */
export const getClientListPath = (archived: boolean = false): string =>
  `/${getActiveBrandConfig()}/tableau-de-bord/${getPathClientBrandConfig()}${archived ? `?${CLIENT_LIST_TAB_PARAM}=${CLIENT_LIST_ARCHIVES_TAB}` : ''}`;

export const getClientDetailPath = (clientId: number | string): string =>
  `/${getActiveBrandConfig()}/tableau-de-bord/${getPathClientBrandConfig()}/${clientId}`;

// ─── Lecture d'une ligne client (ancien format snake_case et nouveau camelCase) ──

export const resolveClientRowFields = (client: any) => {
  const isPending = client?.isValidated === false;
  const clientId = client?.clientId ?? client?.client_id;
  const childId = client?.childId ?? client?.child_id ?? clientId;
  const firstName = client?.firstName ?? client?.child_first_name ?? client?.studentName ?? '';
  const lastName = client?.lastName ?? client?.user_last_name ?? '';
  const isArchive = client?.isArchive ?? client?.is_archive;
  const codeTdp = client?.status ?? client?.code_tdp;
  const descTdp = client?.description ?? client?.description_tdp;
  const contactKey = client?.contactKey ?? client?.key;
  const customFeesId = client?.customFeesId;

  const fullName: string = isPending
    ? capitalizeFirstLetter(firstName) || client?.mail
    : `${capitalizeFirstLetter(firstName)} ${lastName?.toUpperCase()}`;

  const initFullName: string = isPending
    ? (firstName?.charAt(0) || client?.mail?.charAt(0) || '').toUpperCase()
    : `${firstName?.charAt(0)?.toUpperCase() ?? ''}${lastName?.charAt(0)?.toUpperCase() ?? ''}`;

  const dateTime = client?.activationDate ? moment(client.activationDate.replace(/\//g, '-')) : null;

  return { isPending, clientId, childId, firstName, lastName, isArchive, codeTdp, descTdp, contactKey, customFeesId, fullName, initFullName, dateTime };
};

/** Inscription finalisée : le statut n'est pas un des statuts d'inscription en cours. */
export const isRegistrationFinalized = (status: string | null | undefined): boolean =>
  !NON_FINALIZED_STATUSES.includes(status as string);

/**
 * Un client a une fiche dédiée dès qu'il est inscrit (identifiant client
 * connu) et que son inscription est finalisée. Les autres gardent la popup.
 */
export const hasClientDetailPage = (client: any): boolean => {
  const { isPending, clientId, codeTdp } = resolveClientRowFields(client);
  if (isPending || !clientId) return false;
  return isRegistrationFinalized(codeTdp);
};
